import { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { apiPersons, apiPersonManagers, apiPersonWorkSchedule } from '../api/client.js';
import Avatar from './Avatar';
import WorkDaysEditor, { WORK_DAYS, weeklyTotal } from './WorkDaysEditor.jsx';

/**
 * Sprint 14 — Fiche Person en LECTURE SEULE.
 *
 * Réutilisable : ouverte depuis la vue Hiérarchie (un MANAGER consulte la
 * fiche d'un collaborateur sans pouvoir l'éditer). Aucune mutation — pas de
 * bouton "Enregistrer". Charge la Person via `GET /api/persons/:id` (ouvert à
 * tout ACTIVE) + son arborescence managériale directe.
 *
 * Props :
 *  - personId : id de la Person à afficher
 *  - onClose  : callback de fermeture
 */

/** Convertit un `workDaysHours` (objet OU tableau Float [dim,lun,…]) en objet. */
function normalizeWd(wdh) {
  if (!wdh) return null;
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  if (Array.isArray(wdh)) {
    if (wdh.length !== 7) return null;
    return {
      sunday: num(wdh[0]),
      monday: num(wdh[1]),
      tuesday: num(wdh[2]),
      wednesday: num(wdh[3]),
      thursday: num(wdh[4]),
      friday: num(wdh[5]),
      saturday: num(wdh[6]),
    };
  }
  const out = {};
  for (const d of WORK_DAYS) out[d.key] = num(wdh[d.key]);
  return out;
}

const EMPLOYEE_TYPE_LABELS = {
  EMPLOYEE: 'Salarié',
  CONTRACTOR: 'Sous-traitant',
};

function formatRate(rate) {
  if (rate === null || rate === undefined || rate === '') return '—';
  const n = typeof rate === 'string' ? Number(rate) : rate;
  if (Number.isNaN(n)) return '—';
  return `${n.toFixed(2)} €/h`;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR');
}

const sectionTitle = {
  margin: '18px 0 8px',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};

const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, color: 'var(--text)', wordBreak: 'break-word' }}>
        {children ?? '—'}
      </div>
    </div>
  );
}

function Chips({ people, empty }) {
  if (!people || people.length === 0) {
    return <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>{empty}</div>;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {people.map((p) => (
        <span
          key={p.id}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 10px 4px 4px',
            background: 'var(--glass)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-pill)',
            fontSize: 13,
            color: 'var(--text)',
          }}
        >
          <Avatar person={{ firstName: p.firstName, lastName: p.lastName, avatarUrl: p.avatarUrl }} size={20} />
          {p.firstName} {p.lastName}
        </span>
      ))}
    </div>
  );
}

export default function PersonDetailModal({ personId, onClose }) {
  const [person, setPerson] = useState(null);
  const [managers, setManagers] = useState([]);
  const [reports, setReports] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, m, r, ws] = await Promise.all([
        apiPersons.get(personId),
        apiPersonManagers.listManagers(personId).catch(() => ({ items: [] })),
        apiPersonManagers.listReports(personId).catch(() => ({ items: [] })),
        apiPersonWorkSchedule.list(personId).catch(() => ({ items: [] })),
      ]);
      setPerson(p);
      setManagers(m.items ?? []);
      setReports(r.items ?? []);
      setVersions(ws.items ?? []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [personId]);

  useEffect(() => {
    if (!personId) return;
    load();
  }, [personId, load]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const currentVersion = versions.find((v) => v.isCurrent) ?? null;
  const workDays = normalizeWd(currentVersion?.workDaysHours ?? person?.workDaysHours ?? null);

  const fmtDate = (iso) => formatDate(iso);

  return ReactDOM.createPortal(
    <div
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '40px 16px',
        overflowY: 'auto',
        zIndex: 3000,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 600,
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        {/* Header */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--glass-border)',
            background: 'var(--card-bg)',
            borderTopLeftRadius: 'var(--radius-md)',
            borderTopRightRadius: 'var(--radius-md)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            {person ? (
              <Avatar
                person={{ firstName: person.firstName, lastName: person.lastName, avatarUrl: person.avatarUrl }}
                size={36}
              />
            ) : null}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>
                {person ? `${person.firstName} ${person.lastName}` : 'Fiche personne'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Lecture seule{person?.jobTitle ? ` · ${person.jobTitle}` : ''}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Fermer"
            aria-label="Fermer"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: 4,
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '4px 20px 20px' }}>
          {loading ? (
            <div style={{ padding: 20, color: 'var(--text-muted)' }}>Chargement…</div>
          ) : error ? (
            <div style={{ padding: 20, color: 'var(--danger)' }}>{error}</div>
          ) : person ? (
            <>
              <div style={sectionTitle}>Identité</div>
              <div style={grid2}>
                <Field label="Prénom">{person.firstName}</Field>
                <Field label="Nom">{person.lastName}</Field>
                <Field label="Email">{person.email}</Field>
                <Field label="Job title">{person.jobTitle}</Field>
              </div>

              <div style={sectionTitle}>Affectation</div>
              <div style={grid2}>
                <Field label="Rôle">{person.role?.name}</Field>
                <Field label="Département">{person.department?.name}</Field>
                <Field label="Type d'emploi">
                  {EMPLOYEE_TYPE_LABELS[person.employeeType] ?? person.employeeType}
                </Field>
                <Field label="Pays / Région">
                  {person.country
                    ? `${person.country.code}${person.region ? ` — ${person.region.name}` : ''}`
                    : '—'}
                </Field>
              </div>

              <div style={sectionTitle}>Période</div>
              <div style={grid2}>
                <Field label="Date de début">{formatDate(person.startDate)}</Field>
                <Field label="Date de fin">{formatDate(person.endDate)}</Field>
              </div>

              <div style={sectionTitle}>Taux</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <Field label="Bill rate">{formatRate(person.billRate)}</Field>
                <Field label="Cost rate">{formatRate(person.costRate)}</Field>
                <Field label="Tarif Float">{formatRate(person.defaultHourlyRate)}</Field>
              </div>

              {workDays ? (
                <>
                  <div style={sectionTitle}>
                    Disponibilité (heures/jour)
                    {currentVersion ? (
                      <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                        {' '}
                        — en vigueur depuis le {fmtDate(currentVersion.effectiveDate)}
                      </span>
                    ) : null}
                  </div>
                  <WorkDaysEditor value={workDays} readOnly />

                  {versions.length > 1 ? (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                        Historique des versions
                      </div>
                      <div style={{ display: 'grid', gap: 6 }}>
                        {versions.map((v) => {
                          const wd = normalizeWd(v.workDaysHours) ?? {};
                          return (
                            <div
                              key={v.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 8,
                                padding: '6px 10px',
                                background: 'var(--glass)',
                                border: `1px solid ${v.isCurrent ? 'var(--accent)' : 'var(--glass-border)'}`,
                                borderRadius: 'var(--radius-sm)',
                                fontSize: 12,
                              }}
                            >
                              <span style={{ color: 'var(--text)' }}>
                                Dès le {fmtDate(v.effectiveDate)}
                                {v.effectiveTo ? ` → ${fmtDate(v.effectiveTo)}` : ' → en cours'}
                              </span>
                              <span style={{ color: 'var(--text-muted)' }}>{weeklyTotal(wd)} h/sem</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}

              {person.notes ? (
                <>
                  <div style={sectionTitle}>Notes</div>
                  <div style={{ fontSize: 14, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>
                    {person.notes}
                  </div>
                </>
              ) : null}

              <div style={sectionTitle}>Hiérarchie</div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Managers</div>
                <Chips people={managers} empty="Aucun manager affecté" />
              </div>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                  Subordonnés directs <span style={{ opacity: 0.6 }}>({reports.length})</span>
                </div>
                <Chips people={reports} empty="Aucun subordonné direct" />
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
